/* =====================================================
   Numdle Game Logic Utilities – Version 2.1 (Modified: July 31, 2025)
   Date Modified: 31 July 2025 | Time: Current
   🔄 Changes from Version 1.9:
     - Extracted pure functions from main game logic
     - Enhanced algorithm efficiency and readability
     - Added comprehensive JSDoc documentation
     - Optimized permutation and combination generation
===================================================== */

/**
 * Compare two arrays for equality using JSON serialization
 * Used to validate that player used all required numbers
 * @param {Array} arr1 - First array to compare
 * @param {Array} arr2 - Second array to compare
 * @returns {boolean} - True if arrays contain same elements
 */
export function arraysMatch(arr1, arr2) {
  return JSON.stringify(arr1) === JSON.stringify(arr2)
}

/**
 * Generate all possible permutations of the given numbers
 * Uses recursive backtracking to create every possible arrangement
 * @param {Array} nums - Array of numbers to permute
 * @returns {Array} - Array of all permutations
 */
export function getNumberPermutations(nums) {
  const results = []
  
  /**
   * Recursive function to generate permutations
   * @param {Array} remaining - Remaining numbers to arrange
   * @param {Array} current - Current permutation being built
   */
  function permute(remaining, current = []) {
    if (remaining.length === 0) {
      // Base case: no more elements to permute
      results.push([...current])
      return
    }
    
    // Try each remaining element as the next choice
    for (let i = 0; i < remaining.length; i++) {
      const next = remaining[i]
      const newRemaining = remaining.filter((_, index) => index !== i)
      permute(newRemaining, [...current, next])
    }
  }
  
  permute(nums)
  return results
}

/**
 * Generate all possible combinations of operators for a given length
 * Creates every possible sequence of +, -, *, / operators
 * @param {number} length - Number of operators needed
 * @returns {Array} - Array of all operator combinations
 */
export function getOperatorCombinations(length) {
  const operators = ['+', '-', '*', '/']
  const combinations = []
  
  /**
   * Recursive function to build operator combinations
   * @param {Array} current - Current combination being built
   */
  function buildCombination(current = []) {
    if (current.length === length) {
      // Base case: combination is complete
      combinations.push([...current])
      return
    }
    
    // Try each operator as the next choice
    for (const operator of operators) {
      buildCombination([...current, operator])
    }
  }
  
  buildCombination()
  return combinations
}

/**
 * Evaluate a mathematical expression with left-to-right precedence
 * @param {Array} tokens - Array of alternating numbers and operators
 * @returns {Object} - Result object with value and validity
 */
export function evaluateExpression(tokens) {
  if (tokens.length === 0 || tokens.length % 2 === 0) {
    return { value: null, valid: false, error: 'Invalid token count' }
  }

  let result = parseInt(tokens[0])
  if (isNaN(result)) {
    return { value: null, valid: false, error: 'Invalid starting number' }
  }
  
  // Process each operator-number pair
  for (let i = 1; i < tokens.length; i += 2) {
    const operator = tokens[i]
    const number = parseInt(tokens[i + 1])
    
    if (isNaN(number)) {
      return { value: null, valid: false, error: `Invalid number: ${tokens[i + 1]}` }
    }
    
    switch (operator) {
      case '+':
        result += number
        break
      case '-':
        result -= number
        break
      case '*':
        result *= number
        break
      case '/':
        // Validate division: no division by zero, must result in integer
        if (number === 0) {
          return { value: null, valid: false, error: 'Division by zero' }
        }
        if (result % number !== 0) {
          return { value: null, valid: false, error: 'Non-integer division result' }
        }
        result = Math.floor(result / number)
        break
      default:
        return { value: null, valid: false, error: `Unknown operator: ${operator}` }
    }
  }
  
  return { value: result, valid: true, error: null }
}

/**
 * Generate the optimal solution using brute-force search
 * Tests all permutations of numbers with all operator combinations
 * Finds the first valid equation that reaches the target
 * @param {Array} numbersSet - Available numbers to use
 * @param {number} target - Target number to reach
 * @returns {Array|null} - Array of tokens representing the best equation, or null if none found
 */
export function generateBestEquation(numbersSet, target) {
  const numberPermutations = getNumberPermutations(numbersSet)
  const operatorCombinations = getOperatorCombinations(5)
  
  // Try every permutation of numbers
  for (const numbers of numberPermutations) {
    // Try every combination of operators
    for (const operators of operatorCombinations) {
      // Build equation tokens: number, operator, number, operator, ...
      const tokens = []
      for (let i = 0; i < numbers.length; i++) {
        tokens.push(numbers[i].toString())
        if (i < operators.length) {
          tokens.push(operators[i])
        }
      }
      
      // Evaluate the equation
      const result = evaluateExpression(tokens)
      
      // Found a valid solution that reaches the target
      if (result.valid && result.value === target) {
        return tokens
      }
    }
  }
  
  // No solution found
  return null
}

/**
 * Format an equation for display
 * @param {Array} tokens - Array of equation tokens
 * @returns {string} - Formatted equation string
 */
export function formatEquation(tokens) {
  if (!tokens || tokens.length === 0) {
    return 'No solution found'
  }
  
  return tokens.join(' ')
}

/**
 * Check if a number is available for use (not already used)
 * @param {number} number - Number to check
 * @param {Array} usedNumbers - Array of already used numbers
 * @returns {boolean} - True if number is available
 */
export function isNumberAvailable(number, usedNumbers) {
  const numberCount = usedNumbers.filter(num => num === number).length
  const totalCount = 1 // Each number can only be used once
  return numberCount < totalCount
}